import { useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Building2, Upload, Image as ImageIcon, Save } from "lucide-react";
import { validateCabinet } from "./validators.js";
import ActionRequiredBanner from "./ActionRequiredBanner.jsx";
import PreparerNumbersCard from "./PreparerNumbersCard.jsx";
import SignatoryCard from "./SignatoryCard.jsx";

const CABINET_FIELDS = [
  "company_name", "legal_name", "neq",
  "address", "city", "province", "postal_code",
  "phone", "email", "website",
];

export default function CabinetSection({ settings, update, onSave, setSettings }) {
  const [uploading, setUploading] = useState(false);
  const errors = validateCabinet(settings);
  const isValid = Object.keys(errors).length === 0;

  const handleLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/settings/logo", fd);
      setSettings(prev => ({ ...prev, logo_url: res.data.logo_url, _ts: Date.now() }));
      toast.success("Logo televerse");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4">
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="cabinet-section">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <Building2 className="w-4 h-4" /> Informations du cabinet
      </h3>

      {/* Batch #14.52 — Bannière action requise (regroupe Cabinet + SMTP) */}
      <ActionRequiredBanner
        sectionKey="cabinet"
        title="Action requise : configurez votre cabinet"
      />

      <div className="flex items-center gap-4">
        {settings.logo_url ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}?t=${settings._ts || ""}`} alt="Logo"
            className="w-16 h-16 rounded-xl object-contain border"
            onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center border">
            <ImageIcon className="w-6 h-6 text-gray-400" />
          </div>
        )}
        <label className="border px-3 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 flex items-center gap-2">
          <Upload className="w-4 h-4" /> {uploading ? "..." : "Televerser logo"}
          <input type="file" accept="image/*" className="hidden" onChange={handleLogo} data-testid="logo-upload" />
        </label>
      </div>

      <Field label="Nom du cabinet" value={settings.company_name} onChange={v => update("company_name", v)}
        placeholder="Nom de votre entreprise" testId="settings-company-name" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Nom legal (sur les factures)" value={settings.legal_name} onChange={v => update("legal_name", v)}
          placeholder="Nom legal / denomination officielle" testId="settings-legal-name" />
        <Field label="NEQ" value={settings.neq} onChange={v => update("neq", v)}
          placeholder="10 chiffres" error={errors.neq} testId="settings-neq" />
      </div>

      <Field label="Adresse" value={settings.address} onChange={v => update("address", v)}
        placeholder="Numero et rue" testId="settings-address" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Ville" value={settings.city} onChange={v => update("city", v)}
          placeholder="Ville" testId="settings-city" />
        <Field label="Province" value={settings.province} onChange={v => update("province", v)}
          placeholder="QC" testId="settings-province" />
        <Field label="Code postal" value={settings.postal_code} onChange={v => update("postal_code", v.toUpperCase())}
          placeholder="A1A 1A1" error={errors.postal_code} testId="settings-postal-code" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Telephone" value={settings.phone} onChange={v => update("phone", v)}
          placeholder="(514) 555-1234" error={errors.phone} testId="settings-phone" />
        <Field label="Courriel" type="email" value={settings.email} onChange={v => update("email", v)}
          placeholder="contact@entreprise.ca" error={errors.email} testId="settings-email" />
      </div>

      <Field label="Site web" value={settings.website} onChange={v => update("website", v)}
        placeholder="https://..." testId="settings-website" />

      <button onClick={() => onSave(CABINET_FIELDS)} disabled={!isValid}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        data-testid="settings-save-cabinet">
        <Save className="w-4 h-4" /> Sauvegarder
      </button>
      {!isValid && (
        <p className="text-xs text-red-500" data-testid="cabinet-error-hint">Corrigez les champs en rouge avant d'enregistrer.</p>
      )}
    </div>
    <PreparerNumbersCard settings={settings} update={update} onSave={onSave} />
    <SignatoryCard settings={settings} update={update} onSave={onSave} setSettings={setSettings} />
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", error, testId }) {
  const Cls = `w-full border rounded-lg px-3 py-2 text-sm ${error ? "border-red-300 bg-red-50" : ""}`;
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {type === "textarea" ? (
        <textarea rows={2} value={value || ""} onChange={e => onChange(e.target.value)}
          className={Cls} placeholder={placeholder} data-testid={testId} />
      ) : (
        <input type={type} value={value || ""} onChange={e => onChange(e.target.value)}
          className={Cls} placeholder={placeholder} data-testid={testId} />
      )}
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}
